/**
 * saveDiagnostics — builds the redacted "Send the report" payload
 * (Firehose.svelte's one-tap field diagnostic, POSTed to /api/diag).
 *
 * Redaction is by construction: buildDiagReport lists every field it emits
 * explicitly and never spreads a larger object through, so a caller cannot
 * accidentally widen the payload by passing in something extra. The Worker
 * (web/src/worker.ts, handleDiag) independently re-validates and rebuilds
 * the same field list server-side — this module is the client-side half of
 * that same redaction boundary, not the only one.
 *
 * NEVER send label/stroke data, full UUIDs, device names, or health samples.
 */
import type { SaveOutcome } from "./liveCorrection";

export interface DiagSaveLogEntry {
  ok: boolean;
  reason: string;
  atMs: number;
  swimIdPrefix: string;
}

export interface DiagReport {
  build: string;
  loadedFrom: string;
  online: boolean;
  pillShowing: boolean;
  labelJournalCount: number;
  goalJournalCount: number;
  saveStatus: string;
  swimIdPrefix: string;
  note?: string;
  saveLog: DiagSaveLogEntry[];
}

export interface BuildDiagReportInput {
  commitSha: string;
  loadedFrom: string;
  online: boolean;
  pillShowing: boolean;
  labelJournalCount: number;
  goalJournalCount: number;
  saveStatus: string;
  swimUuid: string | undefined;
  saveLog: SaveOutcome[];
  note?: string;
}

const SAVE_LOG_CAP = 20;

/** First 8 characters of a swim UUID only — never send the full id. */
function swimIdPrefix(uuid: string | undefined): string {
  return (uuid ?? "").slice(0, 8);
}

export function buildDiagReport(input: BuildDiagReportInput): DiagReport {
  const report: DiagReport = {
    build: input.commitSha.slice(0, 64),
    loadedFrom: input.loadedFrom.slice(0, 200),
    online: input.online,
    pillShowing: input.pillShowing,
    labelJournalCount: input.labelJournalCount,
    goalJournalCount: input.goalJournalCount,
    saveStatus: input.saveStatus.slice(0, 16),
    swimIdPrefix: swimIdPrefix(input.swimUuid),
    saveLog: input.saveLog.slice(-SAVE_LOG_CAP).map((o) => ({
      ok: o.ok,
      reason: o.reason.slice(0, 64),
      atMs: o.ts,
      swimIdPrefix: swimIdPrefix(o.workoutUUID),
    })),
  };
  if (input.note) report.note = input.note.slice(0, 200);
  return report;
}
